import {
  LBButton,
  LBInfoCircleIcon,
  LBSeparator,
  LBTooltip,
  LBTooltipContent,
  LBTooltipTrigger
} from '@linearb/buzz'
import { useState } from 'react'

import type { OrgBuyer } from '../../apis/organizations'

interface BuyersHeaderProps {
  buyers: OrgBuyer[]
}

const PREVIEW_COUNT = 3

const BuyersHeader = ({ buyers }: BuyersHeaderProps) => {
  const [expanded, setExpanded] = useState(false)

  if (buyers.length === 0) {
    return null
  }

  const overflow = Math.max(0, buyers.length - PREVIEW_COUNT)
  const shown = expanded ? buyers : buyers.slice(0, PREVIEW_COUNT)

  return (
    <div className='rounded-md border border-border bg-muted/30 p-4 space-y-3'>
      <div className='flex items-baseline justify-between gap-2'>
        <div className='flex items-center gap-2'>
          <h3 className='text-sm font-semibold uppercase tracking-wide text-muted-foreground'>
            Buyers
          </h3>
          <LBTooltip>
            <LBTooltipTrigger asChild>
              <button
                type='button'
                aria-label='What is a buyer'
                className='cursor-help text-muted-foreground/70'
              >
                <LBInfoCircleIcon aria-hidden='true' className='h-3.5 w-3.5' />
              </button>
            </LBTooltipTrigger>
            <LBTooltipContent className='max-w-xs'>
              <p>
                Salesforce contacts attached to a closed-won opportunity on
                this organization's account who also visited AI Analytics in
                the last 30 days.
              </p>
            </LBTooltipContent>
          </LBTooltip>
        </div>
        <span className='text-xs text-muted-foreground'>
          Visited AI Analytics · last 30 days
        </span>
      </div>

      <ul className='space-y-3'>
        {shown.map(buyer => {
          const hasName =
            buyer.contact_name && buyer.contact_name !== buyer.email
          return (
            <li key={buyer.contact_id} className='text-sm leading-snug'>
              {hasName && (
                <div className='font-medium text-foreground'>
                  {buyer.contact_name}
                  {buyer.title && (
                    <span className='ml-2 font-normal text-muted-foreground'>
                      ({buyer.title})
                    </span>
                  )}
                </div>
              )}
              {!hasName && buyer.title && (
                <div className='text-muted-foreground'>{buyer.title}</div>
              )}
              <a
                href={`mailto:${buyer.email}`}
                target='_blank'
                rel='noopener noreferrer'
                className='text-sm text-primary hover:underline break-all'
              >
                {buyer.email}
              </a>
            </li>
          )
        })}
      </ul>

      {overflow > 0 && (
        <>
          <LBSeparator />
          <LBButton
            variant='ghost'
            size='sm'
            onClick={() => setExpanded(prev => !prev)}
            className='h-7 px-2 text-xs'
          >
            {expanded ? 'Show less' : `Show ${overflow} more`}
          </LBButton>
        </>
      )}
    </div>
  )
}

export default BuyersHeader
